import React, { FC, ReactNode, useState } from "react";
import { createPortal } from "react-dom";

import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import {
  useCreateCustomFieldMutation,
  useUpdateCustomFieldMutation,
} from "../../slices/CustomFieldsSlice";
import {
  CustomField,
  CustomFieldEntity,
  CustomFieldType,
} from "./customFields.types";
import {
  CustomFieldDraft,
  draftError,
  draftFromField,
  draftToPayload,
  emptyDraft,
  ENTITY_LABELS,
  FIELD_TYPE_LABELS,
  NO_ACCESS_MESSAGE,
} from "./customFields.utils";

const INPUT_CLASSES =
  "w-full rounded-[10px] border border-[#E2E6EE] bg-white px-[13px] py-[9px] text-[14px] text-[#1F2433] outline-none focus:border-[#6B4EFF] focus:shadow-[0_0_0_3px_rgba(107,78,255,0.12)]";

/** Server error text from an RTK Query error. */
export const apiErrorText = (error: unknown, fallback: string) => {
  const data = (error as { data?: unknown } | undefined)?.data;
  return typeof data === "string" && data.trim() ? data : fallback;
};

const Overlay: FC<{ z: string; onClose: () => void; children: ReactNode }> = ({
  z,
  onClose,
  children,
}) =>
  createPortal(
    <div
      className={`fixed inset-0 ${z} flex items-center justify-center bg-[rgba(24,26,38,0.5)] p-6`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}>
      {children}
    </div>,
    document.body,
  );

/** "You don't have access" — shown to people who can't open Settings. */
export const NoAccessModal: FC<{ onClose: () => void; message?: string }> = ({
  onClose,
  message = NO_ACCESS_MESSAGE,
}) => (
  <Overlay z={"z-[1300]"} onClose={onClose}>
    <div
      role={"alertdialog"}
      aria-label={"You don’t have access"}
      className={
        "w-full max-w-[400px] rounded-2xl bg-white px-[26px] pb-[22px] pt-[26px] text-center shadow-[0_24px_60px_rgba(20,22,34,0.3)]"
      }>
      <NoAccessBody message={message} onClose={onClose} />
    </div>
  </Overlay>
);

export const NoAccessBody: FC<{
  message?: string;
  onClose: () => void;
  buttonLabel?: string;
}> = ({ message = NO_ACCESS_MESSAGE, onClose, buttonLabel = "Got it" }) => (
  <>
    <div
      className={
        "mx-auto mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#FEECEC]"
      }>
      <svg
        width={"26"}
        height={"26"}
        viewBox={"0 0 24 24"}
        fill={"none"}
        stroke={"#E5484D"}
        strokeWidth={"2"}
        strokeLinecap={"round"}
        strokeLinejoin={"round"}>
        <rect x={"3"} y={"11"} width={"18"} height={"11"} rx={"2"} />
        <path d={"M7 11V7a5 5 0 0 1 10 0v4"} />
      </svg>
    </div>
    <div className={"mb-2 text-[17px] font-extrabold text-[#1F2433]"}>
      You don’t have access
    </div>
    <div className={"mb-5 text-[13.5px] leading-[1.5] text-[#6B7185]"}>
      {message}
    </div>
    <button
      type={"button"}
      onClick={onClose}
      className={
        "h-[42px] w-full rounded-[10px] bg-[#6B4EFF] text-[13.5px] font-bold text-white hover:bg-[#5B3FEF]"
      }>
      {buttonLabel}
    </button>
  </>
);

type FieldModalProps = {
  entityType: CustomFieldEntity;
  /** Editing an existing field; otherwise a new one is created. */
  field?: CustomField;
  onClose: () => void;
  onSaved?: (field: CustomField) => void;
};

/** New / Edit custom field (Settings, and the client Edit window). */
export const CustomFieldModal: FC<FieldModalProps> = ({
  entityType,
  field,
  onClose,
  onSaved,
}) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const [draft, setDraft] = useState<CustomFieldDraft>(() =>
    field ? draftFromField(field) : emptyDraft(entityType),
  );
  const [showErrors, setShowErrors] = useState(false);
  const [createField, { isLoading: isCreating, error: createError }] =
    useCreateCustomFieldMutation();
  const [updateField, { isLoading: isUpdating, error: updateError }] =
    useUpdateCustomFieldMutation();
  const saving = isCreating || isUpdating;
  const error = draftError(draft);
  const serverError = createError || updateError;

  const setType = (fieldType: CustomFieldType) =>
    setDraft((current) => ({
      ...current,
      fieldType,
      choices: current.choices.length ? current.choices : [""],
    }));

  const save = async () => {
    setShowErrors(true);
    if (error || saving) return;
    const payload = draftToPayload(draft, orgId, field?.id);
    const result = field
      ? await updateField(payload)
      : await createField(payload);
    if ("data" in result) {
      onSaved?.(result.data);
      onClose();
    }
  };

  return (
    <Overlay z={"z-[1200]"} onClose={onClose}>
      <div
        role={"dialog"}
        aria-label={`${field ? "Edit" : "New"} ${
          ENTITY_LABELS[draft.entityType]
        } custom field`}
        className={
          "flex max-h-[88vh] w-full max-w-[480px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_rgba(20,22,34,0.3)]"
        }>
        <div
          className={
            "flex flex-none items-center justify-between border-b border-[#EEF0F4] px-[22px] py-[18px]"
          }>
          <div className={"text-[16px] font-extrabold text-[#1F2433]"}>
            {field ? "Edit" : "New"} {ENTITY_LABELS[draft.entityType]} custom
            field
          </div>
          <button
            type={"button"}
            aria-label={"Close"}
            onClick={onClose}
            className={
              "inline-flex h-[30px] w-[30px] items-center justify-center rounded-lg text-[#9097A8] hover:bg-[#F1F2F5]"
            }>
            <svg
              width={"17"}
              height={"17"}
              viewBox={"0 0 24 24"}
              fill={"none"}
              stroke={"currentColor"}
              strokeWidth={"2.2"}
              strokeLinecap={"round"}>
              <path d={"M18 6 6 18M6 6l12 12"} />
            </svg>
          </button>
        </div>

        <div className={"min-h-0 flex-1 overflow-y-auto px-[22px] py-5"}>
          <label
            htmlFor={"cf-name"}
            className={
              "mb-[7px] block text-[12.5px] font-semibold text-[#5B6472]"
            }>
            Field name
          </label>
          <input
            id={"cf-name"}
            autoFocus
            value={draft.label}
            maxLength={100}
            placeholder={"e.g. Fiscal year end"}
            onChange={(event) =>
              setDraft((current) => ({ ...current, label: event.target.value }))
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                save();
              }
            }}
            className={INPUT_CLASSES}
          />

          <div
            className={
              "mb-2 mt-4 block text-[12.5px] font-semibold text-[#5B6472]"
            }>
            Field type
          </div>
          <div
            role={"radiogroup"}
            aria-label={"Field type"}
            className={"flex gap-2"}>
            {(Object.keys(FIELD_TYPE_LABELS) as CustomFieldType[]).map(
              (type) => {
                const on = draft.fieldType === type;
                return (
                  <button
                    key={type}
                    type={"button"}
                    role={"radio"}
                    aria-checked={on}
                    onClick={() => setType(type)}
                    className={`flex-1 rounded-[9px] border py-[9px] text-center text-[13px] font-semibold ${
                      on
                        ? "border-[#6B4EFF] bg-[#6B4EFF] text-white"
                        : "border-[#D8DCE6] bg-white text-[#5B6472] hover:border-[#B9AEF5]"
                    }`}>
                    {FIELD_TYPE_LABELS[type]}
                  </button>
                );
              },
            )}
          </div>

          {draft.fieldType === "dropdown" && (
            <div className={"mt-4"}>
              <div
                className={
                  "mb-2 block text-[12.5px] font-semibold text-[#5B6472]"
                }>
                Choices
              </div>
              {draft.choices.map((choice, index) => (
                <div key={index} className={"mb-2 flex gap-2"}>
                  <input
                    value={choice}
                    maxLength={100}
                    aria-label={`Choice ${index + 1}`}
                    placeholder={`Choice ${index + 1}`}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        choices: current.choices.map((c, i) =>
                          i === index ? event.target.value : c,
                        ),
                      }))
                    }
                    className={INPUT_CLASSES}
                  />
                  <button
                    type={"button"}
                    title={"Remove choice"}
                    aria-label={`Remove choice ${index + 1}`}
                    onClick={() =>
                      setDraft((current) => {
                        const choices = current.choices.filter(
                          (_, i) => i !== index,
                        );
                        return {
                          ...current,
                          choices: choices.length ? choices : [""],
                        };
                      })
                    }
                    className={
                      "inline-flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[10px] border border-[#E7E9F0] bg-white text-[#D0473F] hover:bg-[#FDF1F1]"
                    }>
                    <TrashIcon size={16} />
                  </button>
                </div>
              ))}
              <button
                type={"button"}
                onClick={() =>
                  setDraft((current) => ({
                    ...current,
                    choices: [...current.choices, ""],
                  }))
                }
                className={"text-[12.5px] font-semibold text-[#6B4EFF]"}>
                + Add choice
              </button>
            </div>
          )}

          {showErrors && error && (
            <div className={"mt-3 text-[13px] font-semibold text-[#E5484D]"}>
              {error}
            </div>
          )}
          {serverError && !error && (
            <div className={"mt-3 text-[13px] font-semibold text-[#E5484D]"}>
              {apiErrorText(
                serverError,
                "The field couldn’t be saved. Please try again.",
              )}
            </div>
          )}
        </div>

        <div
          className={
            "flex flex-none justify-end gap-[10px] border-t border-[#EEF0F4] bg-[#FCFCFD] px-[22px] py-[15px]"
          }>
          <button
            type={"button"}
            onClick={onClose}
            className={
              "h-10 rounded-[10px] border border-[#DCE0E8] bg-white px-5 text-[13.5px] font-bold text-[#3A4256] hover:bg-[#F7F8FA]"
            }>
            Cancel
          </button>
          <button
            type={"button"}
            onClick={save}
            disabled={saving}
            className={
              "h-10 rounded-[10px] bg-[#6B4EFF] px-[22px] text-[13.5px] font-bold text-white shadow-[0_4px_12px_rgba(107,78,255,0.3)] hover:bg-[#5B3FEF] disabled:opacity-60"
            }>
            {saving ? "Saving…" : field ? "Save changes" : "Create field"}
          </button>
        </div>
      </div>
    </Overlay>
  );
};

/** "Delete this field?" — the field disappears for every client of that type. */
export const DeleteFieldModal: FC<{
  field: CustomField;
  deleting: boolean;
  error?: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ field, deleting, error, onCancel, onConfirm }) => (
  <Overlay z={"z-[1250]"} onClose={onCancel}>
    <div
      role={"alertdialog"}
      aria-label={"Delete custom field"}
      className={
        "w-full max-w-[420px] rounded-2xl bg-white p-6 shadow-[0_24px_60px_rgba(20,22,34,0.3)]"
      }>
      <div className={"mb-2 text-[17px] font-extrabold text-[#1F2433]"}>
        Delete “{field.label}”?
      </div>
      <div className={"mb-5 text-[13.5px] leading-[1.5] text-[#6B7185]"}>
        This field will be removed from every{" "}
        {field.entityType === "individual" ? "individual" : "organization"} and
        its saved values will no longer be shown.
      </div>
      {error && (
        <div className={"mb-3 text-[13px] font-semibold text-[#E5484D]"}>
          {error}
        </div>
      )}
      <div className={"flex justify-end gap-[10px]"}>
        <button
          type={"button"}
          onClick={onCancel}
          className={
            "h-10 rounded-[10px] border border-[#DCE0E8] bg-white px-5 text-[13.5px] font-bold text-[#3A4256] hover:bg-[#F7F8FA]"
          }>
          Cancel
        </button>
        <button
          type={"button"}
          onClick={onConfirm}
          disabled={deleting}
          className={
            "h-10 rounded-[10px] bg-[#E5484D] px-5 text-[13.5px] font-bold text-white hover:bg-[#D13C41] disabled:opacity-60"
          }>
          {deleting ? "Deleting…" : "Delete"}
        </button>
      </div>
    </div>
  </Overlay>
);

export const TrashIcon: FC<{ size?: number }> = ({ size = 15 }) => (
  <svg
    width={size}
    height={size}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={"1.8"}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}>
    <path
      d={
        "M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"
      }
    />
  </svg>
);

export const DragIcon: FC = () => (
  <svg width={"12"} height={"18"} viewBox={"0 0 12 18"} fill={"currentColor"}>
    <circle cx={"3"} cy={"3"} r={"1.4"} />
    <circle cx={"9"} cy={"3"} r={"1.4"} />
    <circle cx={"3"} cy={"9"} r={"1.4"} />
    <circle cx={"9"} cy={"9"} r={"1.4"} />
    <circle cx={"3"} cy={"15"} r={"1.4"} />
    <circle cx={"9"} cy={"15"} r={"1.4"} />
  </svg>
);
